import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Input, PipelineBadge } from '@agms/ui'
import type { PipelineStage } from '@agms/ui'
import { useLeads, PIPELINE_STAGES, LEAD_SOURCES } from '../hooks/useLeads'
import { usePermissions } from '../hooks/useAuth'
import { formatSource, stageColor, STAGE_BORDER_COLORS, formatCurrency as fmtCurrency } from '../lib/format'
import type { Lead, LeadFilters } from '../lib/api'
import { PageHeader, SurfaceCard } from '../components/ui'

/**
 * Lead list page with filtering, pagination, and data table.
 * Uses read replica via backend ->useReadPdo() per CLAUDE.md Section 2.
 */
export function LeadsPage() {
  const { canCreateLeads, canAccessAllLeads } = usePermissions()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // Filter state
  const [filters, setFilters] = useState<LeadFilters>({
    page: 1,
    per_page: 15,
    sort_by: 'created_at',
    sort_dir: 'desc',
    stage: searchParams.get('stage') || undefined,
    lead_source: searchParams.get('lead_source') || undefined,
    validifi_flagged: searchParams.get('validifi_flagged') === 'true' || undefined,
  })

  const [searchInput, setSearchInput] = useState('')

  // Fetch leads with current filters
  const { data, isLoading, error } = useLeads(filters)

  // Handle search submit
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setFilters((prev) => ({ ...prev, search: searchInput, page: 1 }))
  }

  // Handle filter changes
  const handleFilterChange = (key: keyof LeadFilters, value: string | undefined) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value || undefined,
      page: 1, // Reset to first page on filter change
    }))
  }

  // Handle pagination
  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }

  // Handle sort
  const handleSort = (column: string) => {
    setFilters((prev) => ({
      ...prev,
      sort_by: column,
      sort_dir: prev.sort_by === column && prev.sort_dir === 'asc' ? 'desc' : 'asc',
    }))
  }

  // Format date for display
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }



  return (
    <div className="space-y-6">
      <PageHeader
        title="Leads"
        subtitle={canAccessAllLeads() ? 'All leads in the pipeline' : 'Your assigned leads'}
        action={canCreateLeads() ? (
          <Link to="/leads/new">
            <Button>New lead</Button>
          </Link>
        ) : undefined}
      />

      {/* Filters */}
      <SurfaceCard padding="p-4">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex gap-2">
              <Input
                aria-label="Search leads"
                placeholder="Search leads..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-64"
              />
              <Button type="submit" variant="secondary">
                Search
              </Button>
            </form>

            {/* Stage filter */}
            <select
              aria-label="Lead stage filter"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={filters.stage || ''}
              onChange={(e) => handleFilterChange('stage', e.target.value)}
            >
              <option value="">All Stages</option>
              {Object.entries(PIPELINE_STAGES).map(([key, config]) => (
                <option key={key} value={key}>
                  {config.label}
                </option>
              ))}
            </select>

            {/* Lead source filter */}
            <select
              aria-label="Lead source filter"
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={filters.lead_source || ''}
              onChange={(e) => handleFilterChange('lead_source', e.target.value)}
            >
              <option value="">All Sources</option>
              {Object.entries(LEAD_SOURCES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>

            {/* Clear filters */}
            {(filters.search || filters.stage || filters.lead_source || filters.validifi_flagged) && (
              <Button
                variant="ghost"
                onClick={() => {
                  setSearchInput('')
                  setFilters({
                    page: 1,
                    per_page: 15,
                    sort_by: 'created_at',
                    sort_dir: 'desc',
                  })
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
      </SurfaceCard>

      {/* Data Table */}
      <SurfaceCard padding="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading leads...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-red-500">Error loading leads. Please try again.</div>
            </div>
          ) : !data || data.data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-gray-500">No leads found</div>
              {canCreateLeads() && (
                <Link to="/leads/new" className="mt-4">
                  <Button>New lead</Button>
                </Link>
              )}
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-line bg-slate-50">
                      <th
                        className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700"
                        onClick={() => handleSort('dba_name')}
                      >
                        Business
                        {filters.sort_by === 'dba_name' && (
                          <span className="ml-1">{filters.sort_dir === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Contact
                      </th>
                      <th className="hidden px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 lg:table-cell">
                        MID
                      </th>
                      <th
                        className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700"
                        onClick={() => handleSort('stage')}
                      >
                        Stage
                        {filters.sort_by === 'stage' && (
                          <span className="ml-1">{filters.sort_dir === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Monthly Vol.
                      </th>
                      <th
                        className="hidden cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700 lg:table-cell"
                        onClick={() => handleSort('lead_source')}
                      >
                        Source
                        {filters.sort_by === 'lead_source' && (
                          <span className="ml-1">{filters.sort_dir === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                        Assigned To
                      </th>
                      <th
                        className="cursor-pointer px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 hover:text-gray-700"
                        onClick={() => handleSort('created_at')}
                      >
                        Created
                        {filters.sort_by === 'created_at' && (
                          <span className="ml-1">{filters.sort_dir === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {data.data.map((lead: Lead) => {
                      const isFlagged = lead.validifi_flagged === true
                      const borderColor = isFlagged
                        ? '#f59e0b'
                        : (STAGE_BORDER_COLORS[stageColor(lead.stage)] ?? '#9ca3af')
                      return (
                        <tr
                          key={lead.id}
                          className={`cursor-pointer hover:bg-brand-50/50 ${isFlagged ? 'bg-amber-50/40' : ''}`}
                          style={{ borderLeft: `4px solid ${borderColor}` }}
                          onClick={() => navigate(`/leads/${lead.id}`)}
                        >
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <div>
                                <Link
                                  to={`/leads/${lead.id}`}
                                  className="font-medium text-ink hover:text-brand-700"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {lead.dba_name || lead.legal_name || 'Unnamed Lead'}
                                </Link>
                                {lead.legal_name && lead.dba_name && (
                                  <div className="text-sm text-gray-500">{lead.legal_name}</div>
                                )}
                              </div>
                              {isFlagged && (
                                <span title="ValidiFI flagged — bank validation needs review" className="flex-shrink-0">
                                  <svg className="h-4 w-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="text-sm">
                              <div className="text-gray-900">{lead.contact_name || '-'}</div>
                              <div className="text-gray-500">{lead.business_email || '-'}</div>
                            </div>
                          </td>
                          <td className="hidden px-4 py-4 text-sm font-mono text-gray-900 lg:table-cell">
                            {lead.application?.fiserv_mid || '-'}
                          </td>
                          <td className="px-4 py-4">
                            <PipelineBadge stage={lead.stage as PipelineStage} size="sm" />
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-900">
                            {fmtCurrency(lead.monthly_volume) ?? '-'}
                          </td>
                          <td className="hidden px-4 py-4 text-sm text-gray-500 lg:table-cell">
                            {formatSource(lead.lead_source) || '-'}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            <div className="text-gray-900">
                              {lead.assigned_user?.name || '-'}
                            </div>
                            {lead.assigned_user_secondary && (
                              <div className="text-xs text-gray-400">
                                + {lead.assigned_user_secondary.name}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-500">
                            {formatDate(lead.created_at)}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
                <div className="text-sm text-gray-500">
                  Showing {data.from || 0} to {data.to || 0} of {data.total} leads
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={data.current_page === 1}
                    onClick={() => handlePageChange(data.current_page - 1)}
                  >
                    Previous
                  </Button>
                  <span className="flex items-center px-3 text-sm text-gray-700">
                    Page {data.current_page} of {data.last_page}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={data.current_page === data.last_page}
                    onClick={() => handlePageChange(data.current_page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
      </SurfaceCard>
    </div>
  )
}

export default LeadsPage
